import { dateToCalendarStateIndex } from '@/helpers/calendarHelper';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import {
  AvailabilityException,
  WorkingPatternDay,
  useAvailabilityExceptionsForEmployeeQuery,
  useEmployeeIdAppliedWorkingPatternsForPeriodQuery,
  useGetRosteredStaffInDateRangeQuery,
  usePendingAvailabilityExceptionRequestsByEmployeeIdQuery
} from '@/services/gql/graphql.generated';
import { useLocationService } from '@/services/location/LocationService';
import { strings } from '@/services/translation/strings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmployeeSchedule, EmployeeScheduleDay } from './schedule.types';
import { useBuildSchedule } from './useBuildSchedule';

export const useEmployeeSchedule = (userId: string, startDate: Date, endDate: Date) => {
  const [{ data: myAvailabilityData }, refetchExceptions] = useAvailabilityExceptionsForEmployeeQuery({
    requestPolicy: 'network-only',
    variables: { userId, startDate: dateToApiFormat(startDate), endDate: dateToApiFormat(endDate) }
  });
  const exceptions = useMemo(
    () => (myAvailabilityData?.availabilityExceptions as AvailabilityException[]) ?? [],
    [myAvailabilityData]
  );

  const [{ data: myWorkingPatternData }, refetchPatterns] = useEmployeeIdAppliedWorkingPatternsForPeriodQuery({
    requestPolicy: 'network-only',
    variables: { id: userId, dateAndPeriods: {} }
  });

  const workingPatternPeriods = myWorkingPatternData?.appliedWorkingPatternsByEmployeeId?.periods || [];

  if (workingPatternPeriods.length === 0) {
    throw Error('Employee working pattern data should be setup');
  }

  const firstWorkingPatternPeriod = workingPatternPeriods[0];
  const workingPatterns = firstWorkingPatternPeriod.patterns;

  if (!workingPatterns) {
    throw Error('Patterns should be defined.');
  }

  const patterns = useMemo(() => workingPatterns as WorkingPatternDay[], [workingPatterns]);

  const [{ data: pendingRequestsData }, refetchPendingRequests] =
    usePendingAvailabilityExceptionRequestsByEmployeeIdQuery({
      variables: { id: userId },
      requestPolicy: 'network-only'
    });
  const pendingRequests = pendingRequestsData?.pendingAvailabilityExceptionRequestsByEmployeeId ?? [];

  const locationId = useLocationService(state => state.selectedLocation?.id);

  if (!locationId) {
    throw Error('Location should be selected to show calendar');
  }

  const [{ data: rosterData }, refetchRosteredStaff] = useGetRosteredStaffInDateRangeQuery({
    variables: {
      startDate: dateToApiFormat(startDate),
      endDate: dateToApiFormat(endDate),
      locationId
    },
    requestPolicy: 'cache-and-network'
  });

  const rosteredStaffInDateRange = rosterData?.rosteredStaffInDateRange || [];

  const rosteredStaff = useMemo(
    () => rosteredStaffInDateRange.filter(s => s.identityId === userId),
    [rosteredStaffInDateRange, userId]
  );

  const [fetching, setFetching] = useState<boolean>(false);
  const refetch = useCallback(async () => {
    setFetching(true);
    await refetchExceptions({ requestPolicy: 'network-only' });
    await refetchPatterns({ requestPolicy: 'network-only' });
    await refetchRosteredStaff({ requestPolicy: 'network-only' });
    await refetchPendingRequests({ requestPolicy: 'network-only' });
    setFetching(false);
  }, [refetchExceptions, refetchPatterns, refetchRosteredStaff]);

  const buildSchedule = useBuildSchedule(startDate, endDate);

  const builtSchedule = useMemo(
    () => buildSchedule(patterns, exceptions, rosteredStaff, pendingRequests),
    [buildSchedule, patterns, exceptions, rosteredStaff, refetchPendingRequests]
  );

  const prevScheduleRef = useRef<EmployeeSchedule | null>(null);

  useEffect(() => {
    const hasChanged = JSON.stringify(prevScheduleRef.current) !== JSON.stringify(builtSchedule);
    if (hasChanged) {
      prevScheduleRef.current = builtSchedule;
      setSchedule(builtSchedule);
    }
  }, [builtSchedule]);

  const [schedule, setSchedule] = useState<EmployeeSchedule>(() => builtSchedule);

  const { registerConfirmation } = useConfirm(state => ({ registerConfirmation: state.registerForConfirmation }));

  const setScheduleDay = useCallback(
    (scheduleDay: EmployeeScheduleDay) => {
      setSchedule(currentSchedule => ({
        ...currentSchedule,
        [dateToCalendarStateIndex(scheduleDay.date)]: scheduleDay
      }));
      registerConfirmation(strings.ess.availability.saveConfirm);
    },
    [registerConfirmation]
  );

  return { schedule, setScheduleDay, refetch, fetching };
};
